import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { missionApi } from '../../api/mission.api';
import { ticketApi } from '../../api/ticket.api';
import { useUI } from '../../store/UIContext';
import { PageHeader, Btn, Skeleton, DraftTextarea, Badge } from '../../components/ui/index';
import LeafletRouteMap from '../../components/map/LeafletRouteMap';
import MissionOtpApprovalPanel from '../../components/missions/MissionOtpApprovalPanel';
import MissionPrintForm from '../../components/missions/MissionPrintForm';
import { BASE_MEDIA_URL } from '../../api/client';
import useUserSignature from '../../hooks/useUserSignature';
import { useAuth } from '../../store/AuthContext';
import { usePaneContext } from '../../store/WorkspaceContext';
import { formatPersianDateLong } from '../../utils/persianNumber';

// Every employee on a mission (owner + companions, see employee_list in
// queryAddMission) lands here from "ماموریت‌های من" - but only the mission's
// manager should be able to submit the execution report; everyone else
// should just be able to see it. get_mission_ticket_info (backend) already
// enforces this server-side (queryGetMissionTicketInfo only returns data
// when the requesting employee IS missions.manager_id) - use its
// success/failure to decide which mode to render, instead of trusting the
// frontend to know who the manager is.
export default function MissionResponsePage() {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const pane = usePaneContext();
  // Inside a workspace tab there's no real :id route param (see
  // WorkspacePane.jsx) - the pane provides it directly instead.
  const id = pane?.id ?? routeId;
  // Same "back" from a normal page means real browser back; from inside a
  // tab it means close this tab (requirement 5's reorganize-on-close).
  const goBack = () => (pane ? pane.close() : navigate(-1));
  const { addToast } = useUI();
  const { user } = useAuth();
  const { signatureUrl } = useUserSignature();
  const [isManager, setIsManager] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [response, setResponse] = useState(null);
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [mapData, setMapData] = useState(null);
  const [mapLoading, setMapLoading] = useState(true);
  const [approvalStatus, setApprovalStatus] = useState(null);
  const [debugOtpHint, setDebugOtpHint] = useState(null);

  useEffect(() => {
    let cancelled = false;
    missionApi.getMissionTicketInfo(id)
      .then((r) => {
        if (cancelled) return;
        setIsManager(true);
        setTicket(Array.isArray(r.data) ? r.data[0] : r.data);
      })
      .catch(async () => {
        // Not the manager (or no longer an active manager on this mission) -
        // fall back to a read-only view of the ticket + its response, if
        // any. get_ticket_info/get_ticket_simple_info both go through
        // ticket_status_view's join chain (inner-joins main_ticket_view,
        // a view not defined anywhere in this repo's tracked SQL) - a
        // ticket that chain happens to exclude 501s on both, even though
        // the ticket itself exists. get_ticket_basic_info reads straight
        // from the `tickets` table instead, sidestepping that entirely.
        if (cancelled) return;
        try {
          const ticketRes = await ticketApi.getTicketBasicInfo(id);
          if (cancelled) return;
          setTicket(ticketRes.data);
          setIsManager(false);
        } catch {
          if (!cancelled) addToast('خطا در دریافت اطلاعات ماموریت', 'error');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    ticketApi.getResponseSimpleInfo(id)
      .then((r) => { if (!cancelled && r.data?.description) setResponse(r.data); })
      .catch(() => {});

    // get_current_map returns whichever active mission the logged-in
    // employee currently has a presence row on (source -> destination
    // route, already computed/cached by queryAddMission via GraphHopper) -
    // this works the same for the mission manager, the vehicle owner and
    // any companion, since every participant gets their own presence row.
    missionApi.getCurrentMap()
      .then((r) => { if (!cancelled) setMapData(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setMapLoading(false); });

    missionApi.getApprovalStatus(id)
      .then((r) => { if (!cancelled) setApprovalStatus(r.data); })
      .catch(() => {});

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const submit = async () => {
    if (!reply.trim()) { addToast('متن پاسخ الزامی است', 'error'); return; }
    const submittedText = reply.trim();
    setSubmitting(true);
    try {
      const res = await ticketApi.employeeResponseTicket({ ticket_id: id, response_title: '', response_description: submittedText, files_json: [] });
      const otp = res.data?.mission_otp;
      if (otp?.debug_otp) setDebugOtpHint(otp.debug_otp);
      if (otp) {
        setApprovalStatus({
          mission_id: otp.mission_id,
          approval_status: otp.approval_status,
          recipient_name: otp.recipient_name,
          recipient_phone: otp.recipient_phone,
          company_name: otp.company_name,
          sms_sent: otp.sms_sent,
        });
        if (otp.approval_status === 'pending_otp') {
          addToast(otp.sms_sent ? 'گزارش ارسال شد — کد OTP به مسئول شرکت ارسال شد' : 'گزارش ارسال شد — OTP صادر شد (SMS در دسترس نبود)', 'success');
        } else if (otp.approval_status === 'no_recipient') {
          addToast('گزارش ارسال شد — مسئول تایید برای این شرکت تعریف نشده', 'info');
        } else {
          addToast('پاسخ ماموریت ارسال شد', 'success');
        }
      } else {
        addToast('پاسخ ماموریت ارسال شد', 'success');
      }
      setIsManager(false);
      setResponse({ description: submittedText });
      setReply('');
      try {
        const responseRes = await ticketApi.getResponseSimpleInfo(id);
        if (responseRes.data?.description) setResponse(responseRes.data);
      } catch { /* keep submittedText in response state */ }
    } catch { addToast('خطا در ارسال پاسخ', 'error'); }
    setSubmitting(false);
  };

  const hasRoute = Array.isArray(mapData?.lat) && mapData.lat.length > 0;

  const missionTitle = ticket?.ticket_title || ticket?.title || '';
  const missionReportText = (isManager ? reply : '') || response?.description || '';
  const dateLabel = formatPersianDateLong(new Date());
  const employeeName = approvalStatus?.manager_name
    || [user?.first_name, user?.last_name].filter(Boolean).join(' ')
    || user?.username
    || '';
  const distanceKm = approvalStatus?.estimated_distance_km
    || (mapData?.distance > 0 ? mapData.distance : null);
  const expertSignatureUrl = approvalStatus?.expert_signature_file
    ? `${BASE_MEDIA_URL}${approvalStatus.expert_signature_file}`
    : signatureUrl;
  const approverSignatureUrl = approvalStatus?.recipient_signature_file
    ? `${BASE_MEDIA_URL}${approvalStatus.recipient_signature_file}`
    : null;
  const approvalLabel = approvalStatus?.approval_status === 'approved'
    ? 'تایید شده'
    : approvalStatus?.approval_status === 'pending_otp'
      ? 'در انتظار OTP'
      : approvalStatus?.approval_status === 'no_recipient'
        ? 'بدون مسئول تایید'
        : null;

  const handlePrint = () => window.print();

  return (
    <div className="fade-in">
      <MissionPrintForm
        title={missionTitle}
        missionText={missionReportText}
        employeeName={employeeName}
        dateLabel={dateLabel}
        companyName={approvalStatus?.company_display || approvalStatus?.company_name}
        distanceKm={distanceKm}
        expertSignatureUrl={expertSignatureUrl}
        approverName={approvalStatus?.recipient_name}
        approverSignatureUrl={approverSignatureUrl}
        approvalStatus={approvalLabel}
      />
      <button onClick={()=>goBack()} style={{display:'flex',alignItems:'center',gap:6,color:'#64748b',background:'none',border:'none',cursor:'pointer',fontFamily:'inherit',fontSize:13,marginBottom:20}}>← بازگشت</button>
      <PageHeader title={isManager ? 'پاسخ به ماموریت' : 'گزارش ماموریت'} action={
        !loading ? (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Btn variant="secondary" onClick={handlePrint}>🖨️ چاپ گزارش</Btn>
            {!isManager && <Badge variant="gray">فقط نمایش</Badge>}
          </div>
        ) : null
      } />
      {loading?<Skeleton h={200} radius={14}/>
      :<div className="pj-ticket-grid">
          {/* Main column */}
          <div style={{display:'flex',flexDirection:'column',gap:16}}>
            {ticket&&(
              <div style={{background:'#fff',borderRadius:14,border:'1px solid rgba(35,58,122,0.1)',padding:24}}>
                <h3 style={{fontSize:16,fontWeight:700,color:'#0f172a',marginBottom:10}}>{ticket.ticket_title||ticket.title}</h3>
                <p style={{fontSize:13,color:'#475569',lineHeight:1.7,margin:0,whiteSpace:'pre-wrap'}}>{ticket.ticket_text||ticket.text||ticket.description}</p>
              </div>
            )}

            {(approvalStatus || debugOtpHint) && (
              <MissionOtpApprovalPanel
                ticketId={id}
                initialStatus={approvalStatus}
                debugOtpHint={debugOtpHint}
                onUpdated={setApprovalStatus}
              />
            )}

            {isManager ? (
              <div style={{background:'#fff',borderRadius:14,border:'1px solid rgba(35,58,122,0.1)',padding:24}}>
                <DraftTextarea draftKey={`mission_reply_${id}`} label="گزارش اجرای ماموریت" rows={5} placeholder="نتیجه و گزارش اجرای ماموریت را بنویسید..." onChange={setReply}/>
                <Btn onClick={submit} loading={submitting} style={{marginTop:14}}>ارسال گزارش</Btn>
              </div>
            ) : (
              <div style={{background:'#fff',borderRadius:14,border:'1px solid rgba(35,58,122,0.1)',padding:24}}>
                <h4 style={{fontSize:13,fontWeight:700,color:'#334155',margin:'0 0 10px'}}>گزارش اجرای ماموریت</h4>
                {response
                  ? <p style={{fontSize:13,color:'#475569',lineHeight:1.7,margin:0,whiteSpace:'pre-wrap'}}>{response.description}</p>
                  : <p style={{fontSize:13,color:'#94a3b8',margin:0}}>هنوز گزارشی توسط مسئول ماموریت ثبت نشده است.</p>}
                <p style={{fontSize:11,color:'#94a3b8',margin:'14px 0 0'}}>فقط مسئول ماموریت می‌تواند گزارش را ثبت یا ویرایش کند.</p>
              </div>
            )}
          </div>

          {/* Side column - route map, visible to every mission participant */}
          <div style={{display:'flex',flexDirection:'column',gap:16}}>
            <div style={{background:'#fff',borderRadius:14,border:'1px solid rgba(35,58,122,0.1)',overflow:'hidden'}}>
              <h4 style={{fontSize:13,fontWeight:700,color:'#64748b',padding:'16px 20px 0'}}>مسیر ماموریت</h4>
              {mapLoading ? <Skeleton h={340} radius={0} style={{margin:16}}/>
              : hasRoute ? (
                <div style={{padding:16}}>
                  {/* get_current_map's distance is already in km - LeafletRouteMap's
                      own label divides by 1000 again assuming meters, so
                      convert back up or every real distance shows ~0.00. */}
                  <LeafletRouteMap lat={mapData.lat} lng={mapData.lng} origin={mapData.origin} destination={mapData.destination} distance={mapData.distance * 1000} height={340}/>
                </div>
              ) : (
                <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:220,color:'#94a3b8',fontSize:12,flexDirection:'column',gap:8,padding:16}}>
                  <span style={{fontSize:32}}>🗺️</span>
                  <span>مسیری برای نمایش موجود نیست</span>
                </div>
              )}
            </div>
          </div>
        </div>
      }
    </div>
  );
}
